import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Contract } from '@interfaces/rest/interfaces/contract';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
export interface ContractSignModalConfig {
  title?: string;
  contract: Contract;
  provinceId: number;
}

@Component({
  selector: 'app-contract-sign-modal',
  templateUrl: './contract-sign-modal.component.html',
  styleUrls: ['./contract-sign-modal.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule]
})
export class ContractSignModalComponent implements OnInit, AfterViewInit {
  @Input() config!: ContractSignModalConfig;
  @ViewChild('signatureCanvas', { static: false }) signatureCanvas!: ElementRef<HTMLCanvasElement>;

  private canvas?: HTMLCanvasElement;
  private ctx?: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;

  constructor(
    private modalController: ModalController,
    private utils: DpUtilsService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.initCanvas();
      }, 100);
    });
  }

  /**
   * Инициализация canvas для рисования подписи
   */
  initCanvas() {
    if (!this.signatureCanvas) {
      console.warn('Signature canvas not found');
      return;
    }

    this.canvas = this.signatureCanvas.nativeElement;
    this.ctx = this.canvas.getContext('2d') || undefined;

    if (!this.ctx) {
      console.error('Could not get canvas context');
      return;
    }

    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = rect.width * dpr;
    this.canvas.height = 300 * dpr;

    this.ctx.scale(dpr, dpr);

    this.canvas.style.width = rect.width + 'px';
    this.canvas.style.height = '300px';

    this.ctx.strokeStyle = '#000000';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.canvas.addEventListener('mousedown', this.startDrawing.bind(this));
    this.canvas.addEventListener('mousemove', this.draw.bind(this));
    this.canvas.addEventListener('mouseup', this.stopDrawing.bind(this));
    this.canvas.addEventListener('mouseout', this.stopDrawing.bind(this));

    this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
  }

  getCoordinates(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if (!this.canvas) {
      return { x: 0, y: 0 };
    }

    const rect = this.canvas.getBoundingClientRect();

    let clientX: number;
    let clientY: number;

    if ('touches' in event && event.touches.length > 0) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      return { x: 0, y: 0 };
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    this.isDrawing = true;
    const coords = this.getCoordinates(event);
    this.lastX = coords.x;
    this.lastY = coords.y;
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing || !this.ctx) {
      return;
    }

    event.preventDefault();
    const coords = this.getCoordinates(event);

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(coords.x, coords.y);
    this.ctx.stroke();

    this.lastX = coords.x;
    this.lastY = coords.y;
  }

  stopDrawing() {
    this.isDrawing = false;
  }

  handleTouchStart(event: TouchEvent) {
    this.startDrawing(event);
  }

  handleTouchMove(event: TouchEvent) {
    this.draw(event);
  }

  handleTouchEnd(event: TouchEvent) {
    this.stopDrawing();
  }

  clearSignature() {
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  hasSignature(): boolean {
    if (!this.canvas || !this.ctx) {
      return false;
    }

    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    const data = imageData.data;

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) {
        return true;
      }
    }

    return false;
  }

  close() {
    this.modalController.dismiss(null);
  }

  async sign() {
    if (!this.config || !this.config.contract) {
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.contract_info_error'),
        cssClass: 'danger'
      });
      return;
    }

    if (!this.hasSignature()) {
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.please_sign'),
        cssClass: 'warning'
      });
      return;
    }

    if (!this.canvas) {
      await this.utils.showToast({
        message: this.translate.instant('province_contracts.signature_error'),
        cssClass: 'danger'
      });
      return;
    }

    const signatureDataUrl = this.canvas.toDataURL('image/png');
    const signatureBase64 = signatureDataUrl.split(',')[1];

    this.modalController.dismiss({
      signed: true,
      signature: signatureBase64,
      contractId: this.config.contract.id
    });
  }

  getContractTypeLabel(type: string | null): string {
    if (!type) return this.translate.instant('province_contracts.no_type');

    const typeLabels: { [key: string]: string } = {
      'driver_agreement': this.translate.instant('contracts.types.driver_agreement'),
      'service_contract': this.translate.instant('contracts.types.service_contract'),
      'privacy_policy': this.translate.instant('contracts.types.privacy_policy')
    };

    return typeLabels[type] || type;
  }
}
